import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAudience } from '../context/AudienceContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search,
  Heart,
  Brain,
  Zap,
  Activity,
  Pill,
  Calculator,
  FileText,
  MessageCircle,
  ShieldAlert,
  ArrowRight,
  TrendingUp
} from 'lucide-react';

import { API_URL } from '../config';

const quickProtocols = [
  { type: 'ACR', label: 'Arrêt Cardiaque', icon: Heart, color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/30' },
  { type: 'AVC', label: 'AVC', icon: Brain, color: 'text-purple-400', bg: 'bg-purple-400/10', border: 'border-purple-400/30' },
  { type: 'EME', label: 'État de Mal', icon: Zap, color: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30' },
  { type: 'CHOC', label: 'Choc', icon: Activity, color: 'text-cyan-400', bg: 'bg-cyan-400/10', border: 'border-cyan-400/30' },
];

const features = [
  { path: '/medications', label: 'Médicaments', desc: 'Rechercher posologies', icon: Pill, color: 'text-green-400' },
  { path: '/calculator', label: 'Calculateur', desc: 'Doses selon poids', icon: Calculator, color: 'text-blue-400' },
  { path: '/protocols', label: 'Protocoles', desc: 'Guides pas-à-pas', icon: FileText, color: 'text-orange-400' },
  { path: '/chat', label: 'Assistant IA', desc: 'Aide au diagnostic', icon: MessageCircle, color: 'text-cyan-400' },
  { path: '/alerts', label: 'Interactions', desc: 'Vérifier CI', icon: ShieldAlert, color: 'text-red-400' },
];

export default function DashboardPage() {
  const { user, token } = useAuth();
  const { audience, isAdult } = useAudience();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recentMeds, setRecentMeds] = useState([]);

  useEffect(() => {
    fetchRecentMeds();
  }, [audience]);

  const fetchRecentMeds = async () => {
    try {
      const response = await fetch(`${API_URL}/api/medications?audience=${audience}&limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRecentMeds(data.medications || []);
      }
    } catch (error) {
      console.error('Error fetching medications:', error);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/medications?query=${encodeURIComponent(query)}&audience=${audience}&limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.medications || []);
      }
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Welcome Header */}
      <div className="hero-gradient rounded-xl p-6 neon-border">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">
          Bonjour, <span className="text-gradient-cyan">{user?.name?.split(' ')[0] || 'Docteur'}</span>
        </h1>
        <p className="text-[hsl(var(--text-dim))]">
          Mode <span className="text-cyan-400 font-medium">{isAdult ? 'Adulte' : 'Pédiatrique'}</span> activé
        </p>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--muted))]" />
        <Input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Rechercher un médicament, protocole..."
          className="pl-12 py-6 text-lg bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))] focus:border-cyan-400/50"
          data-testid="global-search-input"
        />
        
        {/* Search Results Dropdown */}
        {searchResults.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))] rounded-lg shadow-xl z-50 overflow-hidden">
            {searchResults.map((med) => (
              <Link
                key={med._id}
                to={`/medications/${med._id}`}
                className="flex items-center gap-3 p-3 hover:bg-cyan-400/10 transition-colors border-b border-[hsl(var(--border))] last:border-0"
                data-testid="global-search-item"
                onClick={() => setSearchQuery('')}
              >
                <Pill className="w-4 h-4 text-cyan-400" />
                <div>
                  <p className="font-medium">{med.name}</p>
                  <p className="text-xs text-[hsl(var(--text-dim))]">{med.brand_name}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Quick Protocol Access */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-cyan-400" />
          Protocoles d'urgence
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {quickProtocols.map((protocol) => {
            const Icon = protocol.icon;
            return (
              <Link
                key={protocol.type}
                to={`/protocols?type=${protocol.type}`}
                className={`med-card hover-lift flex flex-col items-center gap-2 p-4 text-center ${protocol.bg} ${protocol.border}`}
                data-testid={`quick-protocol-${protocol.type.toLowerCase()}`}
              >
                <Icon className={`w-8 h-8 ${protocol.color}`} />
                <span className="font-medium text-sm">{protocol.label}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Features Grid */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Fonctionnalités</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <Link
                key={feature.path}
                to={feature.path}
                className="med-card hover-lift p-4"
              >
                <Icon className={`w-6 h-6 ${feature.color} mb-2`} />
                <p className="font-medium text-sm">{feature.label}</p>
                <p className="text-xs text-[hsl(var(--text-dim))]">{feature.desc}</p>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent Medications */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Médicaments ({isAdult ? 'Adulte' : 'Pédiatrique'})</h2>
          <Link to="/medications" className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
            Voir tout <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="space-y-2">
          {recentMeds.map((med) => (
            <Link
              key={med._id}
              to={`/medications/${med._id}`}
              className="med-card hover-lift flex items-center justify-between p-3"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-400/10 flex items-center justify-center">
                  <Pill className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="font-medium">{med.name}</p>
                  <p className="text-xs text-[hsl(var(--text-dim))]">{med.brand_name} • {med.indications?.slice(0, 2).join(', ')}</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-[hsl(var(--muted))]" />
            </Link>
          ))}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="glass-card rounded-lg p-4 border border-amber-500/30">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-400">Avertissement</p>
            <p className="text-xs text-[hsl(var(--text-dim))] mt-1">
              UrgenceMed est une aide à la décision clinique. Toute prescription doit être validée par un médecin. 
              Les données sont issues des référentiels Urg'Ara et peuvent nécessiter une adaptation au contexte local.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
